import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress, Checkbox } from '@mui/material';
import { NAVY, ORANGE, PageHeader, GuidanceBanner } from '../components/widgets.comp';
import { CompletionBar } from './ParticipationByGrade.tab';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import { labelCase } from '../schoolData';

/* The bar's caption is the class name the card sends ("Grade 5 - Class A"),
   as every other by-class chart labels it. dims.section_code ("SEC_6ebb854f")
   is the datahouse's internal id and is only a last resort. */
const classLabel = (item) => labelCase(item.label || item.dims?.section_code || '');

const GradeSelector = ({ options, selected, onChange }) => {
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
        }}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>Select a Grade</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {options.map((o) => {
                    const active = o === selected;
                    return (
                        <Box
                            key={o}
                            onClick={() => onChange(o)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5, py: 0.4, px: 0.75,
                                borderRadius: '8px', 
                                cursor: 'pointer',
                                backgroundColor: active ? 'rgba(255,105,0,0.08)' : 'transparent',
                                '&:hover': {
                                    backgroundColor: active ? 'rgba(255,105,0,0.08)' : 'var(--brand-surface-subtle, #F9FAFB)'
                                },
                            }}
                        >
                            <Checkbox
                                checked={active}
                                size="small"
                                sx={{p: 0.5, color: '#C4C4C4', '&.Mui-checked': {color: ORANGE}}}
                            />
                            <Typography sx={{fontSize: '13px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#4B5563'}}>
                                {labelCase(o)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};


const PARTICIPATION_BY_CLASS_SECTION_CODE = 'S_14';
const CARD_SECTION = PARTICIPATION_BY_CLASS_SECTION_CODE;

const ParticipationByClassTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [selectedGrade, setSelectedGrade] = useState('');

    const s14Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === PARTICIPATION_BY_CLASS_SECTION_CODE
        );
    }, [dashboardData]);

    const cards = s14Section?.cards || [];

    // C_1: ListCard
    const listCard = cards.find(c => c.card_code === 'C_1');

    // C_2: TitleCard
    const titleCard = cards.find(c => c.card_code === 'C_2');
    const headerTitle = titleCard?.title || "Participation by Class";
    const headerSubtitle = titleCard?.footer || "Which class needs a completion follow-up before its scores are trusted?";
    const eyebrow = ''; // titleCard?.header;

    // C_3: BarChartBMCard
    const barChartCard = cards.find(c => c.card_code === 'C_3');
    const items = barChartCard?.items || [];

    // Resolve Grade Selection options
    const gradeOptions = useMemo(() => {
        const listOpts = listCard?.list_objects?.map(obj => obj.name || obj.code) || [];
        if (listOpts.length > 0) return listOpts;

        const uniqueGrades = Array.from(new Set(
            items.map(item => item.dims?.section_level).filter(Boolean)
        ));
        return uniqueGrades;
    }, [listCard, items]);

    // Automatically set default selected grade
    useEffect(() => {
        if (gradeOptions.length > 0 && (!selectedGrade || !gradeOptions.includes(selectedGrade))) {
            setSelectedGrade(gradeOptions[0]);
        }
    }, [gradeOptions, selectedGrade]);

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    if (!s14Section) {
        return (
            <NoDataAvailable />
        );
    }

    // Filter items and calculate threshold & guidelines
    const filteredItems = items.filter(item => !selectedGrade || item.dims?.section_level === selectedGrade);
    const threshold = items[0]?.benchmark_values?.find(b => b.name === 'Threshold')?.value ?? 75;

    const belowThresholdClasses = filteredItems
        .filter(item => item.value < threshold)
        .map(item => `${classLabel(item)} (${Math.round(item.value)}%)`);

    const bannerText = belowThresholdClasses.length > 0
        ? `${belowThresholdClasses.join(', ')} ${belowThresholdClasses.length === 1 ? 'is' : 'are'} below the ${threshold}% threshold. Treat their scores as provisional and follow up to boost participation.`
        : `All classes in ${selectedGrade} have met the ${threshold}% participation threshold.`;

    return (
        <Box>
            <PageHeader
                eyebrow={eyebrow}
                title={headerTitle || `Participation by Class — ${selectedGrade}`}
                subtitle={headerSubtitle}
            />

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={3}>
                    <GradeSelector
                        options={gradeOptions}
                        selected={selectedGrade}
                        onChange={setSelectedGrade}
                    />
                </Grid>
                <Grid item xs={12} md={9}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 3}}>
                            {barChartCard?.title || "Completion Rate by Class"} · {threshold}% threshold
                        </Typography>
                        {filteredItems.map((item, idx) => (
                            <CompletionBar
                                key={idx}
                                label={classLabel(item)}
                                pct={item.value !== undefined && item.value !== null ? Math.round(item.value) : 0}
                                status={item.band || item.data?.band}
                                threshold={threshold}
                            />
                        ))}
                        {filteredItems.length === 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{fontStyle: 'italic'}}>
                                No class records to display for the selected grade.
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <GuidanceBanner label="Recommended action:" text={bannerText}/>
        </Box>
    );
};

export default ParticipationByClassTab;

